// Firebase Firestore service for calendar management
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    query,
    where,
    serverTimestamp,
    arrayUnion,
    arrayRemove,
    writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

const CALENDARS_COLLECTION = 'calendars';
const TASKS_COLLECTION = 'tasks';

// Calendar limits per user tier
export const CALENDAR_LIMITS = {
    free: 2,
    founder: 10,
    paid: 10
};

// Permission levels
export const PERMISSIONS = {
    OWNER: 'owner',
    EDITOR: 'editor',
    VIEWER: 'viewer'
};

/**
 * Generate a random share code for calendar sharing
 * @returns {string} A unique share code (e.g., "ASD309SDFW0R3")
 */
const generateShareCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 13; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
};

// Calendar colors
export const CALENDAR_COLORS = [
    { name: 'Blue', value: 'blue' },
    { name: 'Green', value: 'green' },
    { name: 'Purple', value: 'purple' },
    { name: 'Red', value: 'red' },
    { name: 'Orange', value: 'orange' },
    { name: 'Pink', value: 'pink' },
    { name: 'Teal', value: 'teal' },
    { name: 'Indigo', value: 'indigo' }
];

/**
 * Get all calendars accessible to a user (owned or shared)
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} Array of calendar objects
 */
export const getCalendars = async (userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const calendarsRef = collection(db, CALENDARS_COLLECTION);

        // Query for calendars where user is a member
        // Note: Removed orderBy to avoid needing a composite index
        // We'll sort in memory instead
        const q = query(
            calendarsRef,
            where('members', 'array-contains', userId)
        );

        const querySnapshot = await getDocs(q);
        const calendars = [];

        querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            calendars.push({
                id: docSnap.id,
                ...data,
                // Ensure shareCode exists, generate if missing (for existing calendars)
                shareCode: data.shareCode || generateShareCode(),
                createdAt: data.createdAt?.toDate().toISOString(),
                updatedAt: data.updatedAt?.toDate().toISOString()
            });
        });

        // Update any calendars that didn't have a share code
        const updatePromises = calendars
            .filter(cal => !querySnapshot.docs.find(doc => doc.id === cal.id).data().shareCode)
            .map(cal => {
                const calRef = doc(db, CALENDARS_COLLECTION, cal.id);
                return updateDoc(calRef, { shareCode: cal.shareCode });
            });

        if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
        }

        // Sort by createdAt in memory (newest first)
        calendars.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        return calendars;
    } catch (error) {
        console.error('Error getting calendars from Firestore:', error);
        throw error;
    }
};

/**
 * Get a single calendar by ID
 * @param {string} calendarId - The calendar ID
 * @param {string} userId - The user's ID (for permission check)
 * @returns {Promise<Object>} Calendar object
 */
export const getCalendarById = async (calendarId, userId) => {
    if (!userId || !calendarId) {
        throw new Error('User ID and Calendar ID are required');
    }

    try {
        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);
        const docSnap = await getDoc(calendarRef);

        if (!docSnap.exists()) {
            throw new Error('Calendar not found');
        }

        const data = docSnap.data();

        // Check if user has access to this calendar
        if (!data.members.includes(userId)) {
            throw new Error('Access denied to this calendar');
        }

        // Ensure shareCode exists, generate if missing (for existing calendars)
        let shareCode = data.shareCode;
        if (!shareCode) {
            shareCode = generateShareCode();
            await updateDoc(calendarRef, { shareCode });
        }

        return {
            id: docSnap.id,
            ...data,
            shareCode,
            createdAt: data.createdAt?.toDate().toISOString(),
            updatedAt: data.updatedAt?.toDate().toISOString()
        };
    } catch (error) {
        console.error('Error getting calendar:', error);
        throw error;
    }
};

/**
 * Create a new calendar
 * @param {Object} calendarData - Calendar data (name, description, color)
 * @param {string} userId - The owner's user ID
 * @returns {Promise<Object>} Created calendar object
 */
export const createCalendar = async (calendarData, userId, userTier = 'free') => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const calendarsRef = collection(db, CALENDARS_COLLECTION);

        // Server-side limit check: count calendars the user already owns
        const ownerQuery = query(calendarsRef, where('ownerId', '==', userId));
        const ownerSnapshot = await getDocs(ownerQuery);
        const limit = CALENDAR_LIMITS[userTier] ?? CALENDAR_LIMITS.free;
        if (ownerSnapshot.size >= limit) {
            const limitErr = new Error(
                userTier === 'free'
                    ? `Free accounts are limited to ${limit} calendar. Upgrade to create more.`
                    : `You have reached the maximum of ${limit} calendars.`
            );
            limitErr.code = 'calendar-limit-reached';
            throw limitErr;
        }

        const newCalendar = {
            name: calendarData.name,
            description: calendarData.description || '',
            color: calendarData.color || 'blue',
            ownerId: userId,
            members: [userId], // Owner is automatically a member
            permissions: {
                [userId]: PERMISSIONS.OWNER
            },
            shareCode: generateShareCode(), // Generate unique share code
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        const docRef = await addDoc(calendarsRef, newCalendar);

        return {
            id: docRef.id,
            ...newCalendar,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error creating calendar:', error);
        throw error;
    }
};

/**
 * Update a calendar (name, description, color)
 * @param {string} calendarId - The calendar ID
 * @param {Object} updates - Fields to update
 * @param {string} userId - The user's ID (must be owner or editor)
 * @returns {Promise<Object>} Updated calendar object
 */
export const updateCalendar = async (calendarId, updates, userId) => {
    if (!userId || !calendarId) {
        throw new Error('User ID and Calendar ID are required');
    }

    try {
        // First, check permissions
        const calendar = await getCalendarById(calendarId, userId);
        const userPermission = calendar.permissions[userId];

        if (userPermission !== PERMISSIONS.OWNER && userPermission !== PERMISSIONS.EDITOR) {
            throw new Error('Insufficient permissions to update calendar');
        }

        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);

        const allowedUpdates = {
            ...(updates.name && { name: updates.name }),
            ...(updates.description !== undefined && { description: updates.description }),
            ...(updates.color && { color: updates.color }),
            updatedAt: serverTimestamp()
        };

        await updateDoc(calendarRef, allowedUpdates);

        return {
            ...calendar,
            ...allowedUpdates,
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error updating calendar:', error);
        throw error;
    }
};

/**
 * Delete a calendar (only owner can delete)
 * @param {string} calendarId - The calendar ID
 * @param {string} userId - The user's ID (must be owner)
 * @returns {Promise<void>}
 */
export const deleteCalendar = async (calendarId, userId) => {
    if (!userId || !calendarId) {
        throw new Error('User ID and Calendar ID are required');
    }

    try {
        // Check if user is owner
        const calendar = await getCalendarById(calendarId, userId);

        if (calendar.ownerId !== userId) {
            throw new Error('Only the calendar owner can delete it');
        }

        const batch = writeBatch(db);

        // Delete the calendar
        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);
        batch.delete(calendarRef);

        // Delete all tasks in this calendar
        const tasksRef = collection(db, TASKS_COLLECTION);
        const tasksQuery = query(tasksRef, where('calendarId', '==', calendarId));
        const tasksSnapshot = await getDocs(tasksQuery);

        tasksSnapshot.forEach((taskDoc) => {
            batch.delete(taskDoc.ref);
        });

        await batch.commit();
    } catch (error) {
        console.error('Error deleting calendar:', error);
        throw error;
    }
};

/**
 * Share calendar with a user (add member with permission)
 * @param {string} calendarId - The calendar ID
 * @param {string} targetUserId - The user ID to share with
 * @param {string} permission - Permission level (editor or viewer)
 * @param {string} currentUserId - The current user's ID (must be owner)
 * @returns {Promise<Object>} Updated calendar object
 */
export const shareCalendar = async (calendarId, targetUserId, permission, currentUserId) => {
    if (!currentUserId || !calendarId || !targetUserId || !permission) {
        throw new Error('All parameters are required');
    }

    if (![PERMISSIONS.EDITOR, PERMISSIONS.VIEWER].includes(permission)) {
        throw new Error('Invalid permission level. Use "editor" or "viewer"');
    }

    try {
        // Check if current user is owner
        const calendar = await getCalendarById(calendarId, currentUserId);

        if (calendar.ownerId !== currentUserId) {
            throw new Error('Only the calendar owner can share it');
        }

        // Check if target user is already a member
        if (calendar.members.includes(targetUserId)) {
            throw new Error('User is already a member of this calendar');
        }

        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);

        await updateDoc(calendarRef, {
            members: arrayUnion(targetUserId),
            [`permissions.${targetUserId}`]: permission,
            updatedAt: serverTimestamp()
        });

        return {
            ...calendar,
            members: [...calendar.members, targetUserId],
            permissions: {
                ...calendar.permissions,
                [targetUserId]: permission
            },
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error sharing calendar:', error);
        throw error;
    }
};

/**
 * Regenerate share code for a calendar
 * @param {string} calendarId - The calendar ID
 * @param {string} userId - The user's ID (must be owner)
 * @returns {Promise<string>} The new share code
 */
export const regenerateShareCode = async (calendarId, userId) => {
    if (!userId || !calendarId) {
        throw new Error('User ID and Calendar ID are required');
    }

    try {
        // Check if current user is owner
        const calendar = await getCalendarById(calendarId, userId);

        if (calendar.ownerId !== userId) {
            throw new Error('Only the calendar owner can regenerate the share code');
        }

        const newShareCode = generateShareCode();
        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);

        await updateDoc(calendarRef, {
            shareCode: newShareCode,
            updatedAt: serverTimestamp()
        });

        return newShareCode;
    } catch (error) {
        console.error('Error regenerating share code:', error);
        throw error;
    }
};

/**
 * Regenerate share codes for all calendars owned by a user
 * Called on login to ensure privacy and prevent code leakage
 * @param {string} userId - The user's ID
 * @returns {Promise<void>}
 */
export const regenerateAllOwnedShareCodes = async (userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const calendarsRef = collection(db, CALENDARS_COLLECTION);
        const q = query(calendarsRef, where('ownerId', '==', userId));
        const querySnapshot = await getDocs(q);

        const batch = writeBatch(db);

        querySnapshot.forEach((docSnap) => {
            const calendarRef = doc(db, CALENDARS_COLLECTION, docSnap.id);
            batch.update(calendarRef, {
                shareCode: generateShareCode(),
                updatedAt: serverTimestamp()
            });
        });

        if (!querySnapshot.empty) {
            await batch.commit();
        }
    } catch (error) {
        console.error('Error regenerating all share codes:', error);
        throw error;
    }
};

/**
 * Join a calendar using a share code
 * @param {string} shareCode - The calendar share code
 * @param {string} userId - The user ID trying to join
 * @returns {Promise<Object>} The calendar object
 */
export const joinCalendarByCode = async (shareCode, userId) => {
    if (!userId || !shareCode) {
        throw new Error('User ID and share code are required');
    }

    try {
        // Query for calendar with matching share code
        const calendarsRef = collection(db, CALENDARS_COLLECTION);
        const q = query(calendarsRef, where('shareCode', '==', shareCode.trim().toUpperCase()));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            throw new Error('Invalid share code. Please check and try again.');
        }

        const calendarDoc = querySnapshot.docs[0];
        const calendar = { id: calendarDoc.id, ...calendarDoc.data() };

        // Check if user is already a member
        if (calendar.members.includes(userId)) {
            throw new Error('You are already a member of this calendar');
        }

        // Add user as viewer by default
        const calendarRef = doc(db, CALENDARS_COLLECTION, calendar.id);
        await updateDoc(calendarRef, {
            members: arrayUnion(userId),
            [`permissions.${userId}`]: PERMISSIONS.VIEWER,
            updatedAt: serverTimestamp()
        });

        return {
            ...calendar,
            members: [...calendar.members, userId],
            permissions: {
                ...calendar.permissions,
                [userId]: PERMISSIONS.VIEWER
            },
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error joining calendar by code:', error);
        throw error;
    }
};

/**
 * Update member permission in a calendar
 * @param {string} calendarId - The calendar ID
 * @param {string} targetUserId - The user ID whose permission to update
 * @param {string} newPermission - New permission level
 * @param {string} currentUserId - The current user's ID (must be owner)
 * @returns {Promise<Object>} Updated calendar object
 */
export const updateMemberPermission = async (calendarId, targetUserId, newPermission, currentUserId) => {
    if (!currentUserId || !calendarId || !targetUserId || !newPermission) {
        throw new Error('All parameters are required');
    }

    if (![PERMISSIONS.EDITOR, PERMISSIONS.VIEWER].includes(newPermission)) {
        throw new Error('Invalid permission level');
    }

    try {
        const calendar = await getCalendarById(calendarId, currentUserId);

        if (calendar.ownerId !== currentUserId) {
            throw new Error('Only the calendar owner can update permissions');
        }

        if (targetUserId === currentUserId) {
            throw new Error('Cannot change your own permissions');
        }

        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);

        await updateDoc(calendarRef, {
            [`permissions.${targetUserId}`]: newPermission,
            updatedAt: serverTimestamp()
        });

        return {
            ...calendar,
            permissions: {
                ...calendar.permissions,
                [targetUserId]: newPermission
            },
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error updating member permission:', error);
        throw error;
    }
};

/**
 * Remove a member from a calendar
 * @param {string} calendarId - The calendar ID
 * @param {string} targetUserId - The user ID to remove
 * @param {string} currentUserId - The current user's ID (must be owner)
 * @returns {Promise<Object>} Updated calendar object
 */
export const removeMember = async (calendarId, targetUserId, currentUserId) => {
    if (!currentUserId || !calendarId || !targetUserId) {
        throw new Error('All parameters are required');
    }

    try {
        const calendar = await getCalendarById(calendarId, currentUserId);

        if (calendar.ownerId !== currentUserId) {
            throw new Error('Only the calendar owner can remove members');
        }

        if (targetUserId === currentUserId) {
            throw new Error('Cannot remove yourself from your own calendar');
        }

        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);

        await updateDoc(calendarRef, {
            members: arrayRemove(targetUserId),
            [`permissions.${targetUserId}`]: null,
            updatedAt: serverTimestamp()
        });

        return {
            ...calendar,
            members: calendar.members.filter(id => id !== targetUserId),
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Error removing member:', error);
        throw error;
    }
};

/**
 * Leave a shared calendar (member removes themselves)
 * @param {string} calendarId - The calendar ID
 * @param {string} userId - The user's ID
 * @returns {Promise<void>}
 */
export const leaveCalendar = async (calendarId, userId) => {
    if (!userId || !calendarId) {
        throw new Error('User ID and Calendar ID are required');
    }

    try {
        const calendar = await getCalendarById(calendarId, userId);

        if (calendar.ownerId === userId) {
            throw new Error('Owner cannot leave their own calendar. Transfer ownership or delete the calendar instead.');
        }

        const calendarRef = doc(db, CALENDARS_COLLECTION, calendarId);

        await updateDoc(calendarRef, {
            members: arrayRemove(userId),
            [`permissions.${userId}`]: null,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error leaving calendar:', error);
        throw error;
    }
};

/**
 * Get user's permission level for a calendar
 * @param {string} calendarId - The calendar ID
 * @param {string} userId - The user's ID
 * @returns {Promise<string>} Permission level
 */
export const getUserPermission = async (calendarId, userId) => {
    try {
        const calendar = await getCalendarById(calendarId, userId);
        return calendar.permissions[userId] || null;
    } catch (error) {
        console.error('Error getting user permission:', error);
        throw error;
    }
};

/**
 * Check if user can edit in a calendar
 * @param {string} calendarId - The calendar ID
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>}
 */
export const canEdit = async (calendarId, userId) => {
    try {
        const permission = await getUserPermission(calendarId, userId);
        return permission === PERMISSIONS.OWNER || permission === PERMISSIONS.EDITOR;
    } catch (error) {
        return false;
    }
};
